import { useState } from 'react';
import { Link } from 'react-router-dom';

const projectTypes = [
  'Paver Driveway',
  'Paver Patio',
  'Pool Deck',
  'Walkway / Pathway',
  'Retaining Wall',
  'Outdoor Living Space',
  'Other / Not Sure',
];

const trustPoints = [
  { icon: 'ri-phone-fill', label: 'Phone', value: '(813) 404-6313', href: 'tel:813-404-6313' },
  { icon: 'ri-map-pin-fill', label: 'Service Area', value: 'Tampa Bay & Surrounding Areas', href: null },
  { icon: 'ri-time-fill', label: 'Response Time', value: 'Typically within 24 hours', href: null },
];

export default function FinalCTA() {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    project_type: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const body = new URLSearchParams(form as Record<string, string>).toString();
      const res = await fetch('https://readdy.ai/api/form/d6va69e7ipaugi04lhv0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', phone: '', email: '', project_type: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section
      className="py-24 relative bg-parkside-darker"
      style={{
        backgroundImage: "url('https://www.parksidepavers.com/files/projects/15/images/thumb/thumb_large_Albano%2Dlanai%2Ejpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="absolute inset-0 bg-parkside-darker/88" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Value prop */}
          <div className="pt-2">
            <div className="inline-flex items-center gap-2 bg-parkside-amber/20 text-parkside-gold text-xs font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full mb-5">
              <i className="ri-time-line" />
              Limited Spring 2026 Availability
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-white mb-4 tracking-display leading-tight">
              Ready to Transform Your Outdoor Space?
            </h2>
            <p className="text-white/65 text-base leading-relaxed mb-8 max-w-lg">
              Get a custom-designed paver solution built for durability and visual impact. Free design consultation and estimates on all projects throughout Tampa Bay.
            </p>

            <div className="space-y-4 mb-8">
              {trustPoints.map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-white/8 border border-white/15 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-parkside-gold text-lg`} />
                  </div>
                  <div>
                    <p className="text-white/45 text-xs font-semibold uppercase tracking-wider">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="text-white font-bold text-sm hover:text-parkside-gold transition-colors cursor-pointer">
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-white font-semibold text-sm">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Guarantee badge */}
            <div className="bg-white/8 border border-white/15 rounded-2xl p-5 flex gap-4 items-center">
              <div className="w-12 h-12 flex items-center justify-center bg-parkside-green/30 rounded-xl flex-shrink-0">
                <i className="ri-shield-check-fill text-parkside-gold text-2xl" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">Quality Assurance Guarantee</p>
                <p className="text-white/55 text-xs mt-0.5 leading-snug">Every project backed by our 100% satisfaction guarantee and ICPI-certified craftsmanship.</p>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border border-white/30 text-white text-sm font-semibold px-6 py-3 rounded-xl hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                View Full Contact Page
                <span className="w-4 h-4 inline-flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-2xl p-8 border border-stone-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center bg-parkside-amber/12 rounded-xl">
                <i className="ri-file-list-3-line text-parkside-amber text-lg" />
              </div>
              <div>
                <h3 className="text-parkside-dark font-bold text-lg leading-tight">Request Your Free Estimate</h3>
                <p className="text-parkside-stone text-xs">No commitment — we&apos;ll respond within 24 hours.</p>
              </div>
            </div>

            {status === 'success' ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 bg-parkside-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-checkbox-circle-fill text-parkside-green text-3xl" />
                </div>
                <p className="text-parkside-dark font-bold text-lg">Request Sent!</p>
                <p className="text-parkside-stone text-sm mt-1">We&apos;ll be in touch within 24 hours.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-3.5">
                <div className="grid grid-cols-2 gap-3.5">
                  <div>
                    <label className="text-xs font-bold text-parkside-dark mb-1.5 block">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      className="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-parkside-dark placeholder-stone-400 focus:outline-none focus:border-parkside-green focus:ring-1 focus:ring-parkside-green/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-parkside-dark mb-1.5 block">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      placeholder="(813) 000-0000"
                      className="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-parkside-dark placeholder-stone-400 focus:outline-none focus:border-parkside-green focus:ring-1 focus:ring-parkside-green/20 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-parkside-dark mb-1.5 block">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="john@email.com"
                    className="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-parkside-dark placeholder-stone-400 focus:outline-none focus:border-parkside-green focus:ring-1 focus:ring-parkside-green/20 transition-all"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-parkside-dark mb-1.5 block">Project Type</label>
                  <select
                    name="project_type"
                    value={form.project_type}
                    onChange={handleChange}
                    className="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-parkside-dark focus:outline-none focus:border-parkside-green focus:ring-1 focus:ring-parkside-green/20 transition-all cursor-pointer bg-white"
                  >
                    <option value="">Select a project type...</option>
                    {projectTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-parkside-dark mb-1.5 block">Message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={3}
                    maxLength={500}
                    placeholder="Tell us about your project..."
                    className="w-full border border-stone-200 rounded-xl px-3.5 py-2.5 text-sm text-parkside-dark placeholder-stone-400 focus:outline-none focus:border-parkside-green focus:ring-1 focus:ring-parkside-green/20 transition-all resize-none"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-red-500 text-xs">Something went wrong. Please try again or call us directly.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full bg-parkside-amber text-white font-bold py-3.5 rounded-xl hover:bg-parkside-amber/90 transition-all disabled:opacity-70 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap text-sm"
                >
                  {status === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Request Free Estimate — No Commitment
                      <span className="w-4 h-4 inline-flex items-center justify-center">
                        <i className="ri-arrow-right-line" />
                      </span>
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center gap-4 pt-1">
                  <div className="flex items-center gap-1.5 text-xs text-parkside-stone">
                    <span className="w-3 h-3 inline-flex items-center justify-center">
                      <i className="ri-shield-check-line text-parkside-green" />
                    </span>
                    Licensed & Insured
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-parkside-stone">
                    <span className="w-3 h-3 inline-flex items-center justify-center">
                      <i className="ri-star-fill text-parkside-amber" />
                    </span>
                    5-Star Rated
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-parkside-stone">
                    <span className="w-3 h-3 inline-flex items-center justify-center">
                      <i className="ri-award-line text-parkside-green" />
                    </span>
                    ICPI Certified
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
